import { useCallback, useEffect, RefObject, useRef } from 'react';

import { VirtuosoHandle } from 'react-virtuoso';

/**
 * Custom hook for handling keyboard navigation scrolling in dropdown components.
 *
 * Supports both regular (DOM-based) and virtualized (Virtuoso) scrolling:
 * - When isVirtualized is true: uses Virtuoso's imperative scrollIntoView API
 * - When isVirtualized is false: uses DOM scrollIntoView on menu children
 *
 * This ensures that when users navigate with arrow keys, the highlighted
 * item remains visible by explicitly scrolling it into view.
 *
 * NOTE: This hook uses a ref-based approach to prevent scrolling on mouse hover.
 * It only scrolls when highlightedIndex changes due to keyboard navigation.
 *
 * @param isOpen - Whether the dropdown is open
 * @param highlightedIndex - Index of the currently highlighted item
 * @param options - Configuration options
 * @param options.isVirtualized - Whether the list is using virtualization
 * @param options.menuRef - Ref to the menu container (MenuList or VirtuosoHandle)
 */
export const useKeyboardNavigationScrolling = (
  isOpen: boolean,
  highlightedIndex: number,
  options: {
    isVirtualized: boolean;
    menuRef: RefObject<HTMLUListElement | VirtuosoHandle | null>;
  }
) => {
  const { isVirtualized, menuRef } = options;
  const previousHighlightedIndexRef = useRef<number>(highlightedIndex);
  const isMouseMovementRef = useRef<boolean>(false);

  // Track mouse movement over menu items
  const handleItemMouseMove = useCallback(() => {
    isMouseMovementRef.current = true;
  }, []);

  const scrollToHighlightedItem = useCallback(() => {
    if (!isOpen || highlightedIndex < 0 || !menuRef.current) return;

    // Don't scroll if the highlight change was caused by mouse movement
    if (isMouseMovementRef.current) {
      isMouseMovementRef.current = false;
      return;
    }

    if (isVirtualized) {
      // Virtuoso path: use imperative API
      (menuRef.current as VirtuosoHandle).scrollIntoView({
        index: highlightedIndex,
        align: 'center',
        behavior: 'auto',
      });
    } else {
      // DOM path: get child element at index and scroll it into view
      const menuElement = menuRef.current as HTMLUListElement;
      const highlightedElement = menuElement.children[
        highlightedIndex
      ] as HTMLElement;

      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      }
    }
  }, [isOpen, highlightedIndex, isVirtualized, menuRef]);

  useEffect(() => {
    // Reset mouse movement flag when highlightedIndex changes
    if (previousHighlightedIndexRef.current !== highlightedIndex) {
      previousHighlightedIndexRef.current = highlightedIndex;
      scrollToHighlightedItem();
    }
  }, [scrollToHighlightedItem, highlightedIndex]);

  return { scrollToHighlightedItem, handleItemMouseMove };
};
